import React, { useEffect, useState } from 'react';
import { Modal, Form, InputNumber, Input, Button, Avatar, Space, Typography, Card, message, theme } from 'antd';
import { EnvironmentOutlined, DollarOutlined, BorderlessTableOutlined } from '@ant-design/icons';
import { Product } from '../../models/Product';
import { updateProduct } from '../../api/Products';

const { Title, Text } = Typography;

interface Props {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const QuickEditModal = ({ product, open, onClose, onSuccess }: Props) => {
  const { token } = theme.useToken(); // Extraemos los colores del tema actual
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (product && open) {
      form.setFieldsValue({
        quantity: product.quantity,
        location: product.location, // Asumiendo que existe en tu modelo Product
      });
    }
  }, [product, open, form]);

  const handleSave = async (values: { quantity: number; location: string }) => {
    if (!product) return;
    setLoading(true);

    try {
      const formData = new FormData();

      if (values.quantity !== undefined) {
        formData.append('quantity', values.quantity.toString());
      }
      if (values.location !== undefined) {
        formData.append('location', values.location);
      }

      await updateProduct(formData, product.id);
      
      message.success('Ubicación y precio actualizados');
      onSuccess();
      onClose();
    } catch (error) {
      message.error('Error al actualizar los datos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<Title level={4} style={{ margin: 0 }}>Edición Rápida</Title>}
      open={open}
      onCancel={onClose}
      centered
      width={450}
      footer={[
        <Button key="back" onClick={onClose}>Cancelar</Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={loading} 
          onClick={() => form.submit()}
          style={{ 
            backgroundColor: token.colorPrimary, 
            borderColor: token.colorPrimary, 
            height: '40px', 
            borderRadius: token.borderRadius 
          }}
        >
          Guardar Cambios
        </Button>,
      ]}
    >
      {product && (
        <Space direction="vertical" size="large" style={{ width: '100%', marginTop: '10px' }}>
          
          {/* Header dinámico con el tema actual */}
          <Card 
            style={{ 
              backgroundColor: token.colorWarningBg, 
              border: `1px solid ${token.colorWarningBorder}`,
              borderRadius: token.borderRadiusLG 
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Space size="middle">
              <Avatar shape="square" size={64} src={product.image} style={{ backgroundColor: token.colorBgContainer }} />
              <div>
                <Title level={5} style={{ margin: 0 }}>{product.name}</Title>
                <Text type="secondary">SKU: {product.id}</Text>
              </div>
            </Space>
          </Card>

          <Form form={form} layout="vertical" onFinish={handleSave} requiredMark={false}>
            <div style={{ display: 'flex', gap: '16px' }}>
              {/* CAMPO: PRECIO */}
              <Form.Item 
                name="quantity" 
                label={<Text strong>Precio Unitario</Text>} 
                style={{ flex: 1 }}
              >
                <InputNumber 
                  prefix={<BorderlessTableOutlined />} 
                  style={{ width: '100%', height: '40px', borderRadius: token.borderRadius }} 
                  step={1}
                  min={0}
                />
              </Form.Item>

              {/* CAMPO: LOCATION (Ubicación en almacén) */}
              <Form.Item 
                name="location" 
                label={<Text strong>Ubicación Almacén</Text>} 
                style={{ flex: 1 }}
              >
                <Input 
                  prefix={<EnvironmentOutlined />} 
                  placeholder="Ej: A-12-3"
                  style={{ width: '100%', height: '40px', borderRadius: token.borderRadius }} 
                />
              </Form.Item>
            </div>
          </Form>

          <div style={{ 
            backgroundColor: token.colorFillAlter, 
            padding: '12px', 
            borderRadius: token.borderRadiusSM 
          }}>
            <Text strong type="secondary" style={{ fontSize: '11px' }}>RESUMEN DE CAMBIOS</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '13px' }}>
              Los cambios en la ubicación son internos, el precio se reflejará en la tienda.
            </Text>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default QuickEditModal;